// Live-event kind vocabulary (same kinds as the old public/live.js panel),
// shared by the feed (panels.ts), the ticker, and the map glyph sprites
// (objects.ts) — its own module because panels already imports objects, so
// the vocabulary living in either would force a cycle.

// Each kind carries a colour used to tint its glyph chip in the feed (and,
// optionally, the age pin) so kinds are scannable at a glance. Mid-tone hues
// chosen to read on both the light and dark themes.
export const UNCLEAR = { glyph: '❓', label: 'Unclear', color: '#8a97a0' };

export const LIVE_KINDS: Record<string, { glyph: string; label: string; color: string }> = {
  launch: { glyph: '🚀', label: 'Launch', color: '#e8963a' },
  impact: { glyph: '💥', label: 'Impact', color: '#e5484d' },
  airstrike: { glyph: '💣', label: 'Airstrike', color: '#d1385f' },
  drone: { glyph: '🛩️', label: 'Drone strike', color: '#8b5cf6' },
  artillery: { glyph: '🧨', label: 'Artillery', color: '#c2542e' },
  interception: { glyph: '🛡️', label: 'Interception', color: '#3b82c4' },
  air_defence: { glyph: '🛡️', label: 'Air defence', color: '#2ea3a3' },
  air_raid_alert: { glyph: '🚨', label: 'Air-raid alert', color: '#e0b019' },
  explosion: { glyph: '💢', label: 'Explosion', color: '#ef6c2e' },
  fire: { glyph: '🔥', label: 'Fire', color: '#e25822' },
  sonic_boom: { glyph: '🔊', label: 'Sonic boom', color: '#7d93a6' },
  overflight: { glyph: '🌠', label: 'Overflight', color: '#9f7aea' },
  aircraft_takeoff: { glyph: '🛫', label: 'Takeoff', color: '#4a90d9' },
  aircraft_landing: { glyph: '🛬', label: 'Landing', color: '#3f9d63' },
  aircraft_activity: { glyph: '✈️', label: 'Aircraft activity', color: '#6b7fd6' },
  naval_activity: { glyph: '🚢', label: 'Naval activity', color: '#2b6cb0' },
  gunfire: { glyph: '🔫', label: 'Gunfire / clashes', color: '#8d6e63' },
  // GROUND CONTROL (migration 054): the two kinds that move the territorial
  // control layer — a capture reassigns the district under the point, an
  // advance makes the holder's ground contested. Neither is a strike.
  capture: { glyph: '🏴', label: 'Capture', color: '#4e6b3a' },
  advance: { glyph: '⚔️', label: 'Ground advance', color: '#a0522d' },
  outage: { glyph: '🔌', label: 'Outage', color: '#607d8b' },
  // POSTURE AND CONSEQUENCE — nothing was hit, and something still happened
  // here. Added with the news lane: the feed was dropping the Erbil Patriot
  // withdrawal, a counter-terror sweep in Sulaymaniyah and a two-day flight
  // suspension across southern Saudi airports as "no physical event", when all
  // three were mappable facts about a named place and one of them was the
  // biggest story of its hour.
  deployment: { glyph: '🚚', label: 'Deployment / withdrawal', color: '#6f9a5e' },
  security_op: { glyph: '🚔', label: 'Security operation', color: '#5a7d9a' },
  closure: { glyph: '⛔', label: 'Closure / suspension', color: '#a05252' },
  spill: { glyph: '🛢️', label: 'Spill / leak', color: '#5d574b' },
  unclear: UNCLEAR,
};

export interface KindGroup { key: string; label: string; kinds: string[] }

/** How the replay's kind filter GROUPS the vocabulary — the questions an
 *  operator watches a conflict through ("the launches, and what answered
 *  them"), not the extractor's taxonomy. Order is display order. Any kind
 *  not named here lands in a trailing "Other" group (kindGroups), so a kind
 *  added to LIVE_KINDS later never vanishes from the popover. */
export const KIND_GROUPS: KindGroup[] = [
  { key: 'strikes', label: 'Launches & strikes',
    kinds: ['launch', 'impact', 'airstrike', 'drone', 'artillery'] },
  { key: 'defence', label: 'Air defence',
    kinds: ['interception', 'air_defence', 'air_raid_alert'] },
  { key: 'blasts', label: 'Explosions & fires',
    kinds: ['explosion', 'fire', 'sonic_boom'] },
  { key: 'movement', label: 'Air & naval movement',
    kinds: ['overflight', 'aircraft_takeoff', 'aircraft_landing',
            'aircraft_activity', 'naval_activity'] },
  { key: 'ground', label: 'Ground & posture',
    kinds: ['gunfire', 'capture', 'advance', 'security_op', 'deployment'] },
  { key: 'disruption', label: 'Disruption',
    kinds: ['outage', 'closure', 'spill'] },
];

/** KIND_GROUPS against the live vocabulary: names that no longer exist are
 *  dropped, and every kind the groups don't name is collected under Other. */
export function kindGroups(): KindGroup[] {
  const named = new Set(KIND_GROUPS.flatMap((g) => g.kinds));
  const groups = KIND_GROUPS
    .map((g) => ({ ...g, kinds: g.kinds.filter((k) => k in LIVE_KINDS) }))
    .filter((g) => g.kinds.length > 0);
  const rest = Object.keys(LIVE_KINDS).filter((k) => !named.has(k));
  if (rest.length) groups.push({ key: 'other', label: 'Other', kinds: rest });
  return groups;
}

/** The glyphs as shipped, frozen at module load: the reset target, and what a
 *  kind returns to when its override is removed. */
export const SHIPPED_GLYPHS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(Object.entries(LIVE_KINDS).map(([k, v]) => [k, v.glyph])));

/** Replace kind glyphs IN PLACE from the operator's map
 *  (`eventMarkerStyles.glyphs`, applied by `setEventMarkerConfig` at config
 *  load). In place on purpose: fifteen call sites read `LIVE_KINDS[kind].glyph`
 *  at render time — the feed, the ticker, the kind filters, the replay, the
 *  sprite bake — and a mutation reaches every one of them where an accessor
 *  would need each one changed and the next one remembered. A kind absent
 *  from the map returns to its shipped glyph. Sprite names do not carry the
 *  glyph, so like every other marker setting a change reaches an open tab on
 *  reload, never by re-applying after the first bake. */
export function applyGlyphOverrides(overrides: Record<string, string>): void {
  for (const [k, v] of Object.entries(LIVE_KINDS)) {
    v.glyph = overrides[k] ?? SHIPPED_GLYPHS[k]!;
  }
}

/** The glyph-sprite/filter-safe kind: anything unknown renders as unclear. */
export function knownKind(kind: unknown): string {
  const k = String(kind ?? 'unclear');
  return k in LIVE_KINDS ? k : 'unclear';
}

export function kindColor(kind: unknown): string {
  return (LIVE_KINDS[String(kind ?? '')] ?? UNCLEAR).color;
}

export type SourceMedia = 'video' | 'photo' | null;

export interface SourceLink {
  channel: string;
  url: string;
  /** Whether that post carried its own footage (telegram.media_kind). Null on
   *  a text-only post AND on anything stored before media detection existed —
   *  absence is "not known to have media", never "known to have none", which
   *  is why nothing ever renders a "no media" mark. */
  media: SourceMedia;
  /** Late coverage (engine attaches these): the post is ABOUT an earlier
   *  event rather than a contemporaneous report of it. */
  coverage: boolean;
  /** Outcome / follow-up detail about an already-reported event, and whether
   *  it CONTRADICTS what was reported ("the interception failed") rather than
   *  merely adding to it. */
  detail: boolean;
  contested: boolean;
}

const MEDIA_GLYPH: Record<string, string> = { video: '▶', photo: '◳' };

/** The glyph for a source's media, or '' — one place, so the feed row, the
 *  event card and the group card can never disagree about what ▶ means. */
export function mediaGlyph(media: SourceMedia): string {
  return media ? MEDIA_GLYPH[media] ?? '' : '';
}

/** How a source channel NAME renders. A Telegram handle gets its @; an RSS
 *  feed's handle IS its URL (migration 049), and "@https://…" is not a name —
 *  the hostname is what a reader knows the outlet by. '' stays '' so callers
 *  keep their own fallback ("post"). */
export function channelLabel(ch: string | null | undefined): string {
  const c = String(ch || '');
  if (!c) return '';
  if (/^https?:\/\//i.test(c)) {
    try { return new URL(c).hostname.replace(/^www\./, ''); } catch { return c; }
  }
  return `@${c}`;
}

export function mediaLabel(s: SourceLink): string {
  const what = s.media === 'video' ? 'video' : s.media === 'photo' ? 'photo' : '';
  if (!what) return '';
  return s.coverage ? `later ${what}` : what;
}

/** What KIND of source this is, for the citation tooltip — "" for an ordinary
 *  contemporaneous report, which is the common case and needs no label. */
export function roleLabel(s: SourceLink): string {
  if (s.contested) return 'contests what was reported';
  if (s.detail) return 'adds outcome/detail';
  if (s.coverage) return 'later coverage';
  return '';
}

/** Source posts behind an event, as linkable {channel, url, media}. Reads the
 *  folded `sources` list the engine stores (mergeInto/clean_event), falling
 *  back to the event's own channel+postUrl. Deduped by url; only entries with
 *  a url. */
export function sourceLinks(attrs: Record<string, unknown>): SourceLink[] {
  const raw = Array.isArray(attrs.sources) && attrs.sources.length
    ? attrs.sources as Record<string, unknown>[]
    : [{ channel: attrs.channel, postUrl: attrs.postUrl, media: attrs.media }];
  const out: SourceLink[] = [];
  const seen = new Set<string>();
  for (const s of raw) {
    const url = String(s?.postUrl ?? '');
    if (!url || seen.has(url)) continue;
    seen.add(url);
    const m = s?.media;
    out.push({
      channel: String(s?.channel ?? '').replace(/^@/, ''),
      url,
      media: m === 'video' || m === 'photo' ? m : null,
      coverage: s?.coverage === true,
      detail: s?.detail === true,
      contested: s?.contested === true,
    });
  }
  return out;
}

/** The strongest media across an event's sources, for a one-glyph summary on
 *  a row that does not list every citation. Video outranks photo. */
export function bestMedia(links: SourceLink[]): SourceMedia {
  if (links.some((s) => s.media === 'video')) return 'video';
  if (links.some((s) => s.media === 'photo')) return 'photo';
  return null;
}
